/**
 * Intake interview: the questions we ask first, the answer shape, and the
 * derivation of incident type / severity / "regimes to ask counsel about".
 *
 * NOTE: nothing in here states a legal deadline. Deadlines live in a verified,
 * human-reviewed dataset (docs/research/federal-rules.json -> Phase 2). Until that
 * ships we only say which regimes MAY apply and tell the user to confirm with counsel.
 * Regime URLs checked against docs/research/federal-rules.md (2026-11-03); that research still needs counsel review.
 */

export const INCIDENT_TYPES = [
  { id: "ransomware", label: "Ransomware / systems locked or encrypted" },
  { id: "account-compromise", label: "Email or account compromise (stolen password, phishing, takeover)" },
  { id: "data-exposed", label: "Data exposed or stolen (misconfiguration, leak, bulk download)" },
  { id: "lost-device", label: "Lost or stolen laptop, phone, or drive" },
  { id: "vendor", label: "A vendor or partner told us they were breached" },
  { id: "insider", label: "Someone inside the organization (employee or contractor)" },
  { id: "bec-fraud", label: "Fraudulent payment / wire / invoice scam" },
  { id: "unsure", label: "I'm not sure what happened yet" },
] as const
export type IncidentTypeId = (typeof INCIDENT_TYPES)[number]["id"]

export const DATA_TYPES = [
  { id: "contact", label: "Names and contact details (for example email, phone, or mailing address)" },
  { id: "phi", label: "Health information (patient or health-plan data)", hint: "HIPAA / PHI" },
  { id: "card", label: "Payment card numbers", hint: "PCI" },
  { id: "ssn", label: "Social Security or government ID numbers" },
  { id: "financial", label: "Bank or financial account numbers" },
  { id: "credentials", label: "Usernames and passwords / access keys" },
  { id: "employee", label: "Employee or HR records" },
  { id: "children", label: "Children's data" },
  { id: "biometric", label: "Biometric data" },
  { id: "ip", label: "Trade secrets / confidential business data" },
  { id: "none-known", label: "None that I know of / not sure yet" },
] as const
export type DataTypeId = (typeof DATA_TYPES)[number]["id"]

export const ORG_TYPES = [
  { id: "hipaa-ce", label: "Healthcare provider, health plan, or clearinghouse (HIPAA covered entity)" },
  { id: "hipaa-ba", label: "Vendor that handles health data for others (HIPAA business associate)" },
  { id: "public-company", label: "Publicly traded company (SEC registrant)" },
  { id: "financial", label: "Bank, credit union, lender, insurer, broker, tax preparer, or other financial firm" },
  { id: "federal-contractor", label: "Federal or defense contractor" },
  { id: "critical-infrastructure", label: "Energy, utilities, transportation, water, healthcare facility, or other critical infrastructure" },
  { id: "telecom", label: "Telephone or telecom carrier / VoIP provider" },
  { id: "school", label: "School, college, or education provider" },
  { id: "none", label: "None of these / small business or nonprofit" },
] as const
export type OrgTypeId = (typeof ORG_TYPES)[number]["id"]

export const SYSTEMS = [
  { id: "email", label: "Email / Microsoft 365 / Google Workspace" },
  { id: "endpoints", label: "Laptops and desktops" },
  { id: "servers", label: "Servers / file shares" },
  { id: "cloud", label: "Cloud apps or infrastructure (SaaS, AWS, Azure…)" },
  { id: "pos", label: "Payment / point-of-sale systems" },
  { id: "website", label: "Website or customer portal" },
  { id: "backups", label: "Backups" },
  { id: "unknown", label: "Don't know yet" },
] as const
export type SystemId = (typeof SYSTEMS)[number]["id"]

export const US_STATES: readonly { code: string; name: string }[] = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"],
  ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"], ["DC", "District of Columbia"],
  ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"],
  ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"],
  ["ME", "Maine"], ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
  ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"],
  ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
  ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"], ["OR", "Oregon"],
  ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"], ["SD", "South Dakota"],
  ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"],
  ["WA", "Washington"], ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
  ["PR", "Puerto Rico"], ["GU", "Guam"], ["VI", "U.S. Virgin Islands"], ["AS", "American Samoa"],
  ["MP", "Northern Mariana Islands"],
].map(([code, name]) => ({ code, name }))

export type Magnitude = "unknown" | "lt-100" | "100-499" | "500-9999" | "10000-plus"
export const MAGNITUDES: { id: Magnitude; label: string }[] = [
  { id: "unknown", label: "I don't know yet" },
  { id: "lt-100", label: "Fewer than 100 people" },
  { id: "100-499", label: "100 – 499 people" },
  { id: "500-9999", label: "500 – 9,999 people" },
  { id: "10000-plus", label: "10,000 or more" },
]

export type YesNoUnknown = "yes" | "no" | "unknown"

export const DEMANDS = [
  { id: "money", label: "Yes, someone demanded money (for example to unlock systems)" },
  { id: "leak", label: "Yes, someone threatened to publish or leak data" },
  { id: "both", label: "Both" },
  { id: "none", label: "No demand or threat so far" },
  { id: "unknown", label: "I'm not sure" },
] as const
export type Demand = (typeof DEMANDS)[number]["id"]
export const PAID_OPTIONS = [
  { id: "yes", label: "Yes" },
  { id: "no", label: "No" },
  { id: "unknown", label: "I'd rather not say, or I'm not sure" },
] as const

export const ANSWER_KEYS = [
  "stillActive", "unauthorizedAccess", "dataTaken", "affectedInUS", "affectedOutsideUS", "encrypted",
  "magnitude", "backupsIntact", "hasInsurance", "hasCounsel", "hasIrProvider", "demand", "paid",
] as const
export type AnswerKey = (typeof ANSWER_KEYS)[number]

export interface Intake {
  incidentType: IncidentTypeId | null
  stillActive: YesNoUnknown
  /** Did someone get into systems, accounts or data without permission? Drives when data-breach clocks start. */
  unauthorizedAccess: YesNoUnknown
  /** Do we know whether information was taken or copied? Drives when data-breach clocks start. */
  dataTaken: YesNoUnknown
  /** ISO timestamps (local time as entered, stored as ISO string). */
  noticedAt: string
  confirmedAt: string
  orgTypes: OrgTypeId[]
  /** Where the org operates / where affected people live. */
  affectedInUS: YesNoUnknown
  affectedStates: string[]
  affectedOutsideUS: YesNoUnknown
  dataTypes: DataTypeId[]
  encrypted: YesNoUnknown
  magnitude: Magnitude
  systems: SystemId[]
  backupsIntact: YesNoUnknown
  hasInsurance: YesNoUnknown
  hasCounsel: YesNoUnknown
  hasIrProvider: YesNoUnknown
  /** Has anyone demanded money or threatened to leak data? */
  demand: Demand
  /** Has any of it already been paid? ("unknown" also covers "I'd rather not say".) */
  paid: YesNoUnknown
  /** Questions the person has actually answered (including "I don't know"). Unanswered ones show no selection. */
  touched: AnswerKey[]
}

export function emptyIntake(): Intake {
  return {
    incidentType: null,
    stillActive: "unknown",
    unauthorizedAccess: "unknown",
    dataTaken: "unknown",
    noticedAt: "",
    confirmedAt: "",
    orgTypes: [],
    affectedInUS: "unknown",
    affectedStates: [],
    affectedOutsideUS: "unknown",
    dataTypes: [],
    encrypted: "unknown",
    magnitude: "unknown",
    systems: [],
    backupsIntact: "unknown",
    hasInsurance: "unknown",
    hasCounsel: "unknown",
    hasIrProvider: "unknown",
    demand: "unknown",
    paid: "unknown",
    touched: [],
  }
}

export type Severity = "critical" | "high" | "moderate" | "unclear"

export interface RegimeFlag {
  id: string
  name: string
  why: string
  /** Where the user can read about it (primary source). */
  url: string
}

export interface Derived {
  severity: Severity
  severityReasons: string[]
  /** Federal regimes that MAY apply. Never a statement of obligation. */
  regimes: RegimeFlag[]
  statesNotCovered: boolean
  nonUS: boolean
  hasSensitiveData: boolean
}

const has = <T,>(arr: T[], v: T) => arr.includes(v)

export function derive(i: Intake): Derived {
  const reasons: string[] = []
  let score = 0

  if (i.stillActive === "yes") { score += 3; reasons.push("The incident may still be active.") }
  if (i.incidentType === "ransomware") { score += 3; reasons.push("Ransomware disrupts operations and usually involves data theft as well.") }
  const sensitive = i.dataTypes.filter((d) => d !== "none-known")
  if (sensitive.length > 0) { score += 2; reasons.push("Sensitive personal or regulated data may be involved.") }
  if (has(i.dataTypes, "phi") || has(i.dataTypes, "ssn") || has(i.dataTypes, "children")) { score += 1 }
  if (i.magnitude === "500-9999" || i.magnitude === "10000-plus") { score += 2; reasons.push("500 or more people may be affected.") }
  if (demandMade(i)) { score += 2; reasons.push(i.demand === "leak" ? "Someone has threatened to leak data." : "Someone has made a demand.") }
  if (i.paid === "yes" && demandMade(i)) { reasons.push("A payment may already have been made.") }
  if (i.backupsIntact === "no" && asksSystems(i.incidentType)) { score += 2; reasons.push("Backups are unavailable or damaged.") }
  if (i.encrypted === "yes" && sensitive.length > 0 && asksEncryption(i.incidentType)) { score -= 1 }

  const unclear = i.incidentType === "unsure" && sensitive.length === 0 && i.stillActive === "unknown"
  const severity: Severity = unclear ? "unclear" : score >= 7 ? "critical" : score >= 4 ? "high" : "moderate"

  const regimes: RegimeFlag[] = []
  const phi = has(i.dataTypes, "phi")
  if (phi && (has(i.orgTypes, "hipaa-ce") || has(i.orgTypes, "hipaa-ba"))) {
    regimes.push({
      id: "hipaa",
      name: "HIPAA Breach Notification Rule",
      why: "You indicated health data and that you're a HIPAA covered entity or business associate.",
      url: "https://www.hhs.gov/hipaa/for-professionals/breach-notification/index.html",
    })
  } else if (phi) {
    regimes.push({
      id: "ftc-hbnr",
      name: "FTC Health Breach Notification Rule",
      why: "Health data is involved but you didn't indicate HIPAA status. The FTC rule covers some non-HIPAA health apps and vendors.",
      url: "https://www.ecfr.gov/current/title-16/chapter-I/subchapter-C/part-318",
    })
  }
  if (has(i.orgTypes, "public-company")) {
    regimes.push({
      id: "sec-8k",
      name: "SEC cybersecurity incident disclosure (Form 8-K Item 1.05)",
      why: "You indicated you are a publicly traded company.",
      url: "https://www.sec.gov/files/rules/final/2023/33-11216.pdf",
    })
  }
  if (has(i.orgTypes, "financial")) {
    regimes.push({
      id: "glba",
      name: "GLBA / FTC Safeguards Rule and financial-regulator incident rules",
      why: "You indicated you are a financial firm. Which rule applies depends on your type (bank, credit union, non-bank lender, broker-dealer, etc.).",
      url: "https://www.ftc.gov/business-guidance/resources/ftc-safeguards-rule-what-your-business-needs-know",
    })
  }
  if (has(i.orgTypes, "federal-contractor")) {
    regimes.push({
      id: "dfars",
      name: "Federal / defense contractor cyber-incident reporting",
      why: "You indicated you are a federal or defense contractor. Your contract clauses may require rapid reporting.",
      url: "https://www.acquisition.gov/dfars/252.204-7012-safeguarding-covered-defense-information-and-cyber-incident-reporting",
    })
  }
  if (has(i.orgTypes, "critical-infrastructure")) {
    regimes.push({
      id: "circia",
      name: "CISA critical-infrastructure incident reporting (CIRCIA) and sector rules",
      why: "You indicated critical infrastructure. CIRCIA's mandatory reporting rule is not yet in force (final rule still pending as of our last check), but sector-specific rules may already apply. Ask counsel.",
      url: "https://www.cisa.gov/topics/cyber-threats-and-advisories/information-sharing/cyber-incident-reporting-critical-infrastructure-act-2022-circia",
    })
  }
  if (has(i.orgTypes, "telecom")) {
    regimes.push({
      id: "fcc",
      name: "FCC data-breach rules for telecom carriers",
      why: "You indicated you are a telecom or VoIP provider.",
      url: "https://www.ecfr.gov/current/title-47/chapter-I/subchapter-B/part-64/subpart-U/section-64.2011",
    })
  }
  if (has(i.dataTypes, "card")) {
    regimes.push({
      id: "pci",
      name: "Payment card brand / acquirer rules (PCI)",
      why: "Card data is involved. This is a contractual obligation with your bank/processor, not a government law. Contact your acquirer promptly.",
      url: "https://www.pcisecuritystandards.org/",
    })
  }

  const usOrUnknown = i.affectedInUS !== "no"
  return {
    severity,
    severityReasons: reasons,
    regimes,
    statesNotCovered: usOrUnknown,
    nonUS: i.affectedOutsideUS === "yes",
    hasSensitiveData: sensitive.length > 0,
  }
}

export function incidentTypeLabel(id: IncidentTypeId | null): string {
  return INCIDENT_TYPES.find((t) => t.id === id)?.label ?? "Not selected"
}

export interface QuestionCopy {
  stillActive: { title: string; hint: string; why: string }
  unauthorizedAccess: { prompt: string; why: string }
  dataTaken: { prompt: string; why: string }
  encrypted: { prompt: string; why: string }
}

const WHY_ACCESS = "Data-breach notification clocks don't start until you know the answer to this."
const WHY_TAKEN = "Data-breach notification clocks don't start until you know the answer to this. Ask your lawyer what counts as knowing."

/**
 * The same three facts are asked differently for each kind of incident, because "is it still happening?"
 * means something different for ransomware than for a lost laptop or a vendor breach. The answers map
 * onto the same fields (stillActive, unauthorizedAccess, dataTaken) so the clocks work the same way.
 */
export function questionCopy(type: IncidentTypeId | null): QuestionCopy {
  switch (type) {
    case "ransomware":
      return {
        stillActive: { title: "Is it still happening?", hint: "Signs it's ongoing: a ransom note still on screen, more systems locking, strange logins right now.", why: "Active incidents need containment first." },
        unauthorizedAccess: { prompt: "Did someone get into your systems without permission?", why: WHY_ACCESS + " If you can't tell yet, answer I don't know." },
        dataTaken: { prompt: "Did they take or copy any information before locking your systems?", why: WHY_TAKEN + " Ransomware groups often steal data first. If you can't tell yet, answer I don't know." },
        encrypted: { prompt: "Before this happened, was the data encrypted so that someone who took it could not read it?", why: "Ransomware locking your files doesn't count: this is about protection that was already in place. It only helps if the keys weren't taken too. Your lawyer will want the facts." },
      }
    case "account-compromise":
      return {
        stillActive: { title: "Does the attacker still have access?", hint: "Signs: sign-ins you don't recognize, mail rules or forwarding you didn't set, password resets you didn't ask for.", why: "If they still have access, lock the account down first." },
        unauthorizedAccess: { prompt: "Did someone sign in to an account that isn't theirs?", why: WHY_ACCESS },
        dataTaken: { prompt: "Did they read, download, or forward any information from the account?", why: WHY_TAKEN + " If you can't tell yet, answer I don't know." },
        encrypted: { prompt: "Was the data encrypted?", why: "Properly encrypted data can change whether you have to notify. Your lawyer will want the facts." },
      }
    case "data-exposed":
      return {
        stillActive: { title: "Is the data still exposed?", hint: "Signs: the storage, database, link, or page can still be reached by anyone with the address.", why: "Exposed data should be locked down first." },
        unauthorizedAccess: { prompt: "Did anyone other than you get into it?", why: WHY_ACCESS + " If the logs don't show this yet, answer I don't know." },
        dataTaken: { prompt: "Was any of it downloaded or copied?", why: WHY_TAKEN + " If the logs don't show this yet, answer I don't know." },
        encrypted: { prompt: "Was the data encrypted?", why: "Properly encrypted data can change whether you have to notify. Your lawyer will want the facts." },
      }
    case "lost-device":
      return {
        stillActive: { title: "Is the device still missing?", hint: "If it has been found or recovered, answer No.", why: "A missing device should be locked or wiped remotely if you can." },
        unauthorizedAccess: { prompt: "Was the device taken by someone (stolen), rather than just misplaced?", why: WHY_ACCESS },
        dataTaken: { prompt: "Is there any sign that someone looked at or copied what was on it?", why: WHY_TAKEN + " Whether it was encrypted and locked matters here. If you can't tell, answer I don't know." },
        encrypted: { prompt: "Was the device encrypted and locked with a passcode or password?", why: "A lost device that was encrypted and locked can change whether you have to notify. Your lawyer will want the facts." },
      }
    case "vendor":
      return {
        stillActive: { title: "Is the vendor's incident still going on?", hint: "Ask the vendor whether they have contained it. If they haven't told you, answer I don't know.", why: "Ask the vendor for updates in writing." },
        unauthorizedAccess: { prompt: "Was your data or your accounts affected by the vendor's incident?", why: WHY_ACCESS + " If the vendor is still investigating or hasn't told you, answer I don't know." },
        dataTaken: { prompt: "Did the vendor say information was taken or copied?", why: WHY_TAKEN + " If they haven't said, answer I don't know." },
        encrypted: { prompt: "Was the data encrypted?", why: "Properly encrypted data can change whether you have to notify. Your lawyer will want the facts." },
      }
    case "insider":
      return {
        stillActive: { title: "Can the person still get into your systems?", hint: "Think accounts, devices, badges, and shared passwords.", why: "Cut off access first, with HR and counsel involved." },
        unauthorizedAccess: { prompt: "Did the person access things they weren't allowed to?", why: WHY_ACCESS + " If you can't tell yet, answer I don't know." },
        dataTaken: { prompt: "Did they copy or take any information?", why: WHY_TAKEN + " If you can't tell yet, answer I don't know." },
        encrypted: { prompt: "Was the data encrypted?", why: "Properly encrypted data can change whether you have to notify. Your lawyer will want the facts." },
      }
    case "bec-fraud":
      return {
        stillActive: { title: "Is the fraud still in progress?", hint: "Signs: a payment hasn't settled yet, or the scammer is still emailing you. Money can often be recalled in the first hours, so call your bank now.", why: "Speed matters for recalling payments." },
        unauthorizedAccess: { prompt: "Did someone get into your email or other accounts, rather than just sending a convincing fake?", why: WHY_ACCESS },
        dataTaken: { prompt: "Did they read or take any information, such as invoices or customer details?", why: WHY_TAKEN + " If you can't tell yet, answer I don't know." },
        encrypted: { prompt: "Was the data encrypted?", why: "Properly encrypted data can change whether you have to notify. Your lawyer will want the facts." },
      }
    default:
      return {
        stillActive: { title: "Is anything still going wrong right now?", hint: "Signs: unusual activity continues, systems are locked or down, odd messages keep arriving.", why: "Active incidents need containment first." },
        unauthorizedAccess: { prompt: "Did someone get into your systems or accounts without permission?", why: WHY_ACCESS + " If you can't tell yet, answer I don't know." },
        dataTaken: { prompt: "Was any information taken or copied?", why: WHY_TAKEN + " If you can't tell yet, answer I don't know." },
        encrypted: { prompt: "Was the data encrypted?", why: "Properly encrypted data can change whether you have to notify. Your lawyer will want the facts." },
      }
  }
}

/** Which intake questions make sense for which kind of incident. */
export const asksEncryption = (t: IncidentTypeId | null) => t !== "account-compromise" && t !== "bec-fraud"
export const asksSystems = (t: IncidentTypeId | null) => t !== "lost-device" && t !== "bec-fraud"

export type QuestionId =
  | "unauthorizedAccess" | "dataTaken" | "encrypted" | "magnitude" | "stillActive" | "dataTypes" | "orgTypes" | "affectedInUS" | "demand" | "paid" | "backupsIntact"

export interface OpenQuestion {
  id: QuestionId
  prompt: string
  /** Plain-language reason this matters. */
  why: string
  /** True when a deadline clock cannot start until this is answered. */
  blocksClock: boolean
}

/**
 * Intake answers that are still "I don't know" / empty and are worth going back to.
 * Answering them starts clocks, changes who must be told, and changes the plan.
 */
export function openQuestions(i: Intake): OpenQuestion[] {
  const q: OpenQuestion[] = []
  const copy = questionCopy(i.incidentType)
  if (i.unauthorizedAccess === "unknown") q.push({ id: "unauthorizedAccess", prompt: copy.unauthorizedAccess.prompt, why: copy.unauthorizedAccess.why, blocksClock: true })
  if (i.dataTaken === "unknown") q.push({ id: "dataTaken", prompt: copy.dataTaken.prompt, why: copy.dataTaken.why, blocksClock: true })
  if (i.dataTypes.length === 0 || (i.dataTypes.length === 1 && i.dataTypes[0] === "none-known")) q.push({ id: "dataTypes", prompt: "What kinds of data might be involved?", why: "Decides which federal rules may apply to you.", blocksClock: false })
  if (i.orgTypes.length === 0) q.push({ id: "orgTypes", prompt: "What kind of organization are you?", why: "Decides which federal rules may apply to you.", blocksClock: false })
  if (i.encrypted === "unknown" && asksEncryption(i.incidentType) && i.dataTypes.some((d) => d !== "none-known")) q.push({ id: "encrypted", prompt: copy.encrypted.prompt, why: copy.encrypted.why, blocksClock: false })
  if (i.magnitude === "unknown") q.push({ id: "magnitude", prompt: "About how many people's data?", why: "Some rules add steps at 500 or more people.", blocksClock: false })
  if (asksDemand(i.incidentType) && !i.touched.includes("demand")) q.push({ id: "demand", prompt: "Has anyone demanded money, or threatened to leak or publish data?", why: "A demand changes the advice: don't reply or pay before talking to your lawyer and law enforcement.", blocksClock: false })
  if (asksDemand(i.incidentType) && demandMade(i) && !i.touched.includes("paid")) q.push({ id: "paid", prompt: "Has any of it already been paid?", why: "Payments can carry sanctions risk. Counsel and law enforcement need to know what was paid, when, and to whom.", blocksClock: false })
  if (i.backupsIntact === "unknown" && !i.touched.includes("backupsIntact") && ["ransomware", "unsure", "data-exposed", "insider", "account-compromise"].includes(i.incidentType ?? "unsure") && i.incidentType !== null) q.push({ id: "backupsIntact", prompt: BACKUPS_TITLE, why: "It decides how safely you can recover.", blocksClock: false })
  if (i.stillActive === "unknown") q.push({ id: "stillActive", prompt: copy.stillActive.title, why: copy.stillActive.why, blocksClock: false })
  if (i.affectedInUS === "unknown") q.push({ id: "affectedInUS", prompt: "Are any affected people in the United States?", why: "State laws also apply to most US incidents (not yet covered here).", blocksClock: false })
  return q
}

/** The value to show for a single-choice question: nothing until the person has answered it. */
export function shown<K extends AnswerKey>(i: Intake, k: K): Intake[K] | null {
  return i.touched.includes(k) ? i[k] : null
}

/** Record an answer and remember that the question has been answered. */
export function withAnswer<K extends AnswerKey>(i: Intake, k: K, v: Intake[K]): Intake {
  return { ...i, [k]: v, touched: i.touched.includes(k) ? i.touched : [...i.touched, k] }
}

/** Workspaces saved before answers were tracked: anything that isn't "unknown" was answered. */
export function inferTouched(i: Omit<Intake, "touched">): AnswerKey[] {
  return ANSWER_KEYS.filter((k) => i[k] !== "unknown")
}

/** One wording for the backups question, used by the wizard and the My answers page. */
export const BACKUPS_TITLE = "Do you have backups you could restore from that the attacker probably can't reach?"
export const BACKUPS_HINT = "For example copies kept offline, in a separate account, or that can't be changed once written."

/** Extortion questions only make sense where an attacker may be making demands. */
export const asksDemand = (t: IncidentTypeId | null) => t === "ransomware" || t === "data-exposed" || t === "unsure"
export const demandMade = (i: Intake) => i.demand === "money" || i.demand === "leak" || i.demand === "both"

// ---- merged "where are the affected people?" question ------------------------------------------
export const GEOGRAPHY = [
  { id: "us", label: "The United States only" },
  { id: "both", label: "The United States and other countries" },
  { id: "nonus", label: "Outside the United States only" },
  { id: "unknown", label: "I'm not sure yet" },
] as const
export type GeographyId = (typeof GEOGRAPHY)[number]["id"]

/** The merged answer, or null until the person has answered it. */
export function geographyOf(i: Intake): GeographyId | null {
  if (!i.touched.includes("affectedInUS") && !i.touched.includes("affectedOutsideUS")) return null
  const us = i.affectedInUS, non = i.affectedOutsideUS
  if (us === "yes" && non === "yes") return "both"
  if (us === "yes") return "us"
  if (us === "no" && non === "yes") return "nonus"
  return "unknown"
}
export function withGeography(i: Intake, g: GeographyId): Intake {
  const map: Record<GeographyId, [YesNoUnknown, YesNoUnknown]> = { us: ["yes", "no"], both: ["yes", "yes"], nonus: ["no", "yes"], unknown: ["unknown", "unknown"] }
  return withAnswer(withAnswer(i, "affectedInUS", map[g][0]), "affectedOutsideUS", map[g][1])
}

// ---- merged "which of these can you call today?" question --------------------------------------
export const HELP_OPTIONS = [
  { id: "insurance", label: "Cyber insurance (including cover added to another business policy)" },
  { id: "counsel", label: "A lawyer experienced with data breaches" },
  { id: "ir", label: "An incident-response or computer-forensics firm" },
  { id: "none", label: "None of these yet" },
] as const
export type HelpId = (typeof HELP_OPTIONS)[number]["id"]

/** Selected help, or null until the person has answered. */
export function helpOf(i: Intake): HelpId[] | null {
  if (!i.touched.some((k) => k === "hasInsurance" || k === "hasCounsel" || k === "hasIrProvider")) return null
  const out: HelpId[] = []
  if (i.hasInsurance === "yes") out.push("insurance")
  if (i.hasCounsel === "yes") out.push("counsel")
  if (i.hasIrProvider === "yes") out.push("ir")
  return out.length ? out : ["none"]
}
/** Picked options are "yes", the rest are "no". An empty pick resets the question to unanswered. */
export function withHelp(i: Intake, ids: readonly string[]): Intake {
  const keys = ["hasInsurance", "hasCounsel", "hasIrProvider"] as const
  if (ids.length === 0) return { ...i, hasInsurance: "unknown", hasCounsel: "unknown", hasIrProvider: "unknown", touched: i.touched.filter((k) => !(keys as readonly string[]).includes(k)) }
  let n = i
  n = withAnswer(n, "hasInsurance", ids.includes("insurance") ? "yes" : "no")
  n = withAnswer(n, "hasCounsel", ids.includes("counsel") ? "yes" : "no")
  n = withAnswer(n, "hasIrProvider", ids.includes("ir") ? "yes" : "no")
  return n
}
